type RuleRecord = Record<string, unknown>

interface CleanRuleOptions {
  /**
   * Editing an existing rule (true) vs. creating a new one (false). Duplicating
   * is a create, so it passes `isEditing: false` too.
   */
  isEditing: boolean
}

const omit = (obj: RuleRecord, keys: string[]): RuleRecord => {
  const result: RuleRecord = {}
  for (const key of Object.keys(obj)) {
    if (!keys.includes(key)) result[key] = obj[key]
  }
  return result
}

// Client-only fields that must never be sent to the create/update mutation.
const CONDITION_CLIENT_FIELDS = ['__typename', 'customStep', 'tag']
const ACTION_CLIENT_FIELDS = [
  '__typename',
  'fcraLegalRequirementsConsent',
  // Display-only relation objects resolved for the read/pipeline views. The
  // scalar `leadEmployeeId` / `memberEmployeeIds` are the persisted config and
  // are kept; these resolved objects must be stripped before the mutation.
  'leadEmployee',
  'memberEmployees',
]
const ACTIVE_HOURS_CLIENT_FIELDS = ['__typename']

/**
 * Strip client-only fields from a rule's conditions before sending them to the
 * create/update mutation.
 *
 * On create — including duplicate, which is just a create seeded from an
 * existing rule — we also drop `conditionId` so the backend inserts brand-new
 * rows. The create resolver does a nested Prisma `create`, so carrying the
 * source rule's ids would collide on the primary key. On edit we keep the id so
 * the existing row is matched and updated in place.
 *
 * Empty placeholder rows (no `value` and no `stepType`) are dropped: the rule
 * builder inserts a null-valued sub-condition (e.g. Terminated Reason) when an
 * applicant stage is selected, and leaving it empty means "any reason". The
 * rules engine groups conditions by key and requires each present group to
 * match, so persisting a null-valued row would wrongly constrain the rule to
 * applicants whose field is null.
 */
export const cleanRuleConditions = (
  conditions: RuleRecord[] | undefined | null,
  { isEditing }: CleanRuleOptions
): RuleRecord[] => {
  const drop = isEditing
    ? CONDITION_CLIENT_FIELDS
    : [...CONDITION_CLIENT_FIELDS, 'conditionId']
  return (conditions ?? [])
    .filter((condition) => condition.value || condition.stepType)
    .map((condition) => omit(condition, drop))
}

/**
 * Strip client-only fields from a rule's actions before sending them to the
 * create/update mutation. `fcraLegalRequirementsConsent` is a UI-only flag and
 * is never persisted. `actionId` is dropped on create/duplicate (see
 * {@link cleanRuleConditions} for the rationale).
 */
export const cleanRuleActions = (
  actions: RuleRecord[] | undefined | null,
  { isEditing }: CleanRuleOptions
): RuleRecord[] => {
  const drop = isEditing
    ? ACTION_CLIENT_FIELDS
    : [...ACTION_CLIENT_FIELDS, 'actionId']
  return (actions ?? []).map((action) => omit(action, drop))
}

/**
 * Strip client-only fields from a rule's active-hours rows before sending them
 * to the create/update mutation. `activeHoursId` is dropped on create/duplicate
 * so the backend inserts brand-new rows (see {@link cleanRuleConditions} for
 * the rationale); on edit it is kept so existing rows update in place.
 */
export const cleanRuleActiveHours = (
  activeHours: RuleRecord[] | undefined | null,
  { isEditing }: CleanRuleOptions
): RuleRecord[] => {
  const drop = isEditing
    ? ACTIVE_HOURS_CLIENT_FIELDS
    : [...ACTIVE_HOURS_CLIENT_FIELDS, 'activeHoursId']
  return (activeHours ?? []).map((row) => omit(row, drop))
}
